"use client";

import { useEffect, useState } from "react";
import { devLabel } from "@/lib/dev-labels";
import { getCredits } from "@/lib/analyze";
import { ModeTabs, type ModeKey } from "./ModeTabs";
import { UrlInputMode } from "./UrlInputMode";
import { FileUploadMode } from "./FileUploadMode";
import { ScriptMode } from "./ScriptMode";
import { MulticamMode } from "./MulticamMode";
import { BatchMode } from "./BatchMode";

export interface MainCreateCardProps {
	/** `mode` is the selected analysis chip label (e.g. "AI + 카테고리 자동"). */
	onAnalyze: (input: string, mode: string) => void;
	/** File-upload mode — analyze a local video File (Increment 14). */
	onFileAnalyze?: (file: File, mode: string) => void;
	onBatchStart?: () => void;
	/** Script mode — generate shorts from a written script via the given engine. */
	onScriptGenerate?: (script: string, engine: string) => void;
	/** Multicam mode — analyze a multi-camera set (one job per camera File). */
	onMulticamAnalyze?: (files: File[]) => void;
}

const CHIP_OPTIONS = ["AI + 카테고리 자동", "AI 자체 판단", "자동 추천", "수동"];

export function MainCreateCard({
	onAnalyze,
	onFileAnalyze,
	onBatchStart,
	onScriptGenerate,
	onMulticamAnalyze,
}: MainCreateCardProps) {
	const [mode, setMode] = useState<ModeKey>("url");
	const [chip, setChip] = useState(CHIP_OPTIONS[0]);
	const [coins, setCoins] = useState<number | null>(null);

	// Real coin balance from the Worker (via same-origin BFF). null = not
	// logged in / not loaded yet -> shown as "로그인 필요".
	useEffect(() => {
		let cancelled = false;
		getCredits().then((c) => {
			if (!cancelled) setCoins(c ? c.balance : null);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	// Pass the selected analysis chip through to the Welcome page so it can
	// be sent as target_clip_count when the job is created.
	const analyzeWithMode = (input: string) => onAnalyze(input, chip);

	return (
		<div
			className="main-create-card"
			data-testid="main-create-card"
			style={{
				gridColumn: "1 / 3",
				minHeight: 220,
				borderRadius: 10,
				background:
					"linear-gradient(135deg,#a8b5e8 0%,#7ac6d8 40%,#5a8aaa 80%,#3a5a7e 100%)",
				padding: "16px 20px",
				cursor: "default",
				position: "relative",
				overflow: "visible",
				display: "flex",
				flexDirection: "column",
				gap: 10,
				boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
			}}
			{...devLabel("11-A")}
		>
			{/* Title row */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					position: "relative",
					zIndex: 2,
				}}
			>
				<div
					style={{
						fontSize: 14,
						fontWeight: 700,
						color: "#fff",
						display: "inline-flex",
						alignItems: "center",
						gap: 6,
					}}
				>
					<svg width="14" height="14" viewBox="0 0 24 24" style={{ color: "#d4af37" }} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
						<line x1="12" y1="5" x2="12" y2="19" />
						<line x1="5" y1="12" x2="19" y2="12" />
					</svg>
					분석 시작
					<span
						style={{
							padding: "2px 6px",
							fontSize: 9,
							fontWeight: 700,
							background: "rgba(212,175,55,0.95)",
							color: "#0a0a0a",
							borderRadius: 3,
							letterSpacing: "0.05em",
						}}
					>
						MML+
					</span>
				</div>
				<span style={{ fontSize: 10, color: "rgba(255,255,255,0.7)" }}>
					{coins === null ? "로그인 필요" : `코인 ${coins} 잔액`}
				</span>
			</div>

			{/* Mode tabs */}
			<ModeTabs active={mode} onChange={setMode} />

			{/* Mode content */}
			<div style={{ position: "relative", zIndex: 2 }}>
				{mode === "url" && <UrlInputMode onAnalyze={analyzeWithMode} />}
				{mode === "file" && (
					<FileUploadMode
						onAnalyze={(file) => onFileAnalyze?.(file, chip)}
					/>
				)}
				{mode === "script" && <ScriptMode onGenerate={onScriptGenerate} />}
				{mode === "multicam" && (
					<MulticamMode onAnalyze={onMulticamAnalyze} />
				)}
				{mode === "batch" && <BatchMode onStartBatch={onBatchStart} />}
			</div>

			{/* Chip row */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 10,
					position: "relative",
					zIndex: 2,
				}}
				{...devLabel("11-A7")}
			>
				{CHIP_OPTIONS.map((c, idx) => {
					const on = chip === c;
					return (
						<button
							key={c}
							type="button"
							onClick={() => setChip(c)}
							style={{
								padding: "4px 10px",
								background: on ? "#fff" : "rgba(0,0,0,0.35)",
								border: on
									? "1px solid #fff"
									: "1px solid rgba(255,255,255,0.2)",
								borderRadius: 999,
								fontSize: 10,
								fontWeight: 600,
								color: on ? "#0a0a0a" : "#fff",
								cursor: "pointer",
								backdropFilter: "blur(8px)",
								display: "inline-flex",
								alignItems: "center",
								gap: 4,
							}}
						>
							{idx === 0 && (
								<svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
									<polygon points="12 2 15 9 22 9.3 17 14 18.5 21 12 17.3 5.5 21 7 14 2 9.3 9 9 12 2" />
								</svg>
							)}
							{c}
						</button>
					);
				})}
			</div>
		</div>
	);
}
